import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { GET_STANDARD_LABELS, UPDATE_LABEL_POLICY_CONTENT } from '@system-config/label-management/graphql';
import { useState, useCallback } from 'react';

interface UpdateLabelPolicyContentResponse {
  updateLabelPolicyContent: {
    success: boolean;
    message: string;
    labelId: string;
    policyFile: string;
  };
}

export function useUpdateLabelPolicyContent() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateLabelPolicyContentMutation, { loading }] = useMutation<UpdateLabelPolicyContentResponse>(UPDATE_LABEL_POLICY_CONTENT);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updateLabelPolicyContent = useCallback(
    async (labelId: string, content: string): Promise<boolean> => {
      try {
        track('UpdateStandardLabelPolicyInitiated', {
          labelId,
        });

        await updateLabelPolicyContentMutation({
          variables: { labelId, content },
          refetchQueries: [{ query: GET_STANDARD_LABELS }],
          awaitRefetchQueries: true,
        });

        track('UpdateStandardLabelPolicySucceeded', {
          labelId,
        });

        return true;
      } catch (error) {
        track('UpdateStandardLabelPolicyFailed', {
          labelId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return false;
      }
    },
    [updateLabelPolicyContentMutation]
  );

  return {
    updateLabelPolicyContent,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateLabelPolicyContent;
